import React, { useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon, CheckIcon } from "@heroicons/react/24/outline";
import { useAuthStore } from "../../store/authStore";
import { useSubscriptionStore } from "../../store/subscriptionStore";
import { useToast } from "../../hooks/useToast";
import { dodoService } from "../../services/dodoService";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "../../lib/supabase";

interface SubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Dodo Payments product IDs
const DODO_PRODUCT_IDS = {
  starter: import.meta.env.VITE_DODO_PRODUCT_ID, // $20/month
  pro: import.meta.env.VITE_DODO_PRODUCT_ID, // $20/month
  enterprise: import.meta.env.VITE_DODO_PRODUCT_ID, // $20/month
};

export const SubscriptionModal: React.FC<SubscriptionModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const user = useAuthStore((state) => state.user);
  const {
    isSubscribed,
    status,
    trialEndsAt,
    isTrialActive,
    checkSubscriptionStatus,
  } = useSubscriptionStore();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const handleSubscribe = async (
    planType: "starter" | "pro" | "enterprise" = "pro"
  ) => {
    try {
      setIsLoading(true);
      if (!user) {
        showToast("Please sign in to subscribe", "error");
        return;
      }

      const productId = DODO_PRODUCT_IDS[planType];
      await dodoService.redirectToCheckout(productId, user.id, user.email);
    } catch (error) {
      showToast("Failed to start checkout process", "error");
      console.error("Checkout error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubscribed && status === "active") {
    return (
      <Transition show={isOpen} as={React.Fragment}>
        <Dialog as="div" className="relative z-50" onClose={onClose}>
          <div className="fixed inset-0 bg-black/30" />
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl">
                <div className="absolute right-4 top-4">
                  <button onClick={onClose}>
                    <XMarkIcon className="h-6 w-6 text-gray-400" />
                  </button>
                </div>

                <Dialog.Title
                  as="h3"
                  className="text-2xl font-bold text-center mb-4"
                >
                  Pro Plan Active
                </Dialog.Title>

                <p className="text-center text-gray-600 mb-8">
                  You already have access to all premium features.
                </p>

                <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-6 mb-8">
                  <div className="flex justify-between items-center">
                    <h4 className="text-xl font-semibold">Pro Plan</h4>
                    <div className="text-right">
                      <div className="text-2xl font-bold">$20</div>
                      <div className="text-sm text-gray-500">per month</div>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 rounded-lg p-4 flex items-center justify-center mb-8">
                  <CheckIcon className="h-5 w-5 text-green-600 mr-2" />
                  <span className="text-green-800">Active Subscription</span>
                </div>

                <button
                  onClick={async () => {
                    try {
                      if (user) {
                        const response = await supabase.functions.invoke(
                          "create-portal-session",
                          {
                            body: {
                              userId: user.id,
                              returnUrl: window.location.origin,
                            },
                          }
                        );

                        if (response.error) {
                          throw new Error(
                            response.error.message ||
                              "Failed to create portal session"
                          );
                        }

                        const data = response.data;
                        if (data?.url) {
                          window.location.href = data.url;
                        } else {
                          throw new Error("No portal URL returned");
                        }
                      }
                    } catch (error) {
                      showToast("Failed to open customer portal", "error");
                      console.error("Portal error:", error);
                    }
                  }}
                  className="w-full py-3 bg-indigo-600 rounded-lg text-white font-medium hover:bg-indigo-700 text-center block mb-4"
                >
                  Manage Billing
                </button>

                <button
                  onClick={onClose}
                  className="w-full py-3 bg-gray-100 rounded-lg text-gray-900 font-medium hover:bg-gray-200"
                >
                  Close
                </button>
              </Dialog.Panel>
            </div>
          </div>
        </Dialog>
      </Transition>
    );
  }

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <div className="fixed inset-0 bg-black/30" />
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl">
              <div className="absolute right-4 top-4">
                <button onClick={onClose}>
                  <XMarkIcon className="h-6 w-6 text-gray-400" />
                </button>
              </div>

              <Dialog.Title
                as="h3"
                className="text-2xl font-bold text-center mb-4"
              >
                {isTrialActive
                  ? "Upgrade to Pro"
                  : status === "inactive" || status === "trialing"
                  ? "Trial Ended - Upgrade Required"
                  : "Upgrade to Pro"}
              </Dialog.Title>

              <p className="text-center text-gray-600 mb-8">
                {isTrialActive ? (
                  <>
                    Your trial ends on{" "}
                    {new Date(trialEndsAt!).toLocaleDateString()}. Upgrade now
                    to keep access to all features.
                  </>
                ) : status === "inactive" ? (
                  "Your trial has ended. Upgrade to Pro to regain access to all features."
                ) : (
                  "Upgrade to Pro to unlock unlimited comments, team collaboration, and premium features."
                )}
              </p>

              <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-6 mb-8">
                <div className="flex justify-between items-center mb-6">
                  <h4 className="text-xl font-semibold">Pro Plan</h4>
                  <div className="text-right">
                    <div className="text-2xl font-bold">$20</div>
                    <div className="text-sm text-gray-500">per month</div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <CheckIcon className="h-5 w-5 text-green-500" />
                    <span className="text-gray-700">Unlimited comments</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckIcon className="h-5 w-5 text-green-500" />
                    <span className="text-gray-700">Team collaboration</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckIcon className="h-5 w-5 text-green-500" />
                    <span className="text-gray-700">Custom branding</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckIcon className="h-5 w-5 text-green-500" />
                    <span className="text-gray-700">Priority support</span>
                  </div>
                </div>
              </div>

              <button
                onClick={() => handleSubscribe("pro")}
                disabled={isLoading}
                className="w-full py-3 bg-indigo-600 rounded-lg text-white font-medium hover:bg-indigo-700 text-center block mb-4 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? "Processing..." : "Upgrade Now"}
              </button>

              <button
                onClick={onClose}
                className="w-full py-3 bg-gray-100 rounded-lg text-gray-900 font-medium hover:bg-gray-200"
              >
                Cancel
              </button>

              <p className="text-center text-gray-500 mt-6 text-sm">
                You can cancel your subscription anytime.
              </p>
            </Dialog.Panel>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
